import { createFileRoute } from "@tanstack/react-router";
import { LANGS, getDict, translateStatic } from "@/lib/i18n";

export const Route = createFileRoute("/qa/mapa-preklady")({
  head: () => ({
    meta: [
      { title: "QA · Překlady mapy" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Interní náhled překladů pro mapu ve všech jazykových verzích." },
    ],
  }),
  component: MapTranslationsQA,
});

/** Klíče použité na stránce /mapa (filtry, popupy, legenda). */
const MAP_UI_KEYS = [
  "mapa.tag",
  "mapa.loading",
  "mapa.showUsers",
  "mapa.showAnon",
  "mapa.breweries",
  "mapa.partnerBadge",
  "mapa.viewProfile",
  "mapa.addBusiness",
  "mapa.kindHeader",
  "mapa.filters.partnership",
  "mapa.filters.city",
  "mapa.filters.allCities",
  "mapa.filters.postal",
  "mapa.filters.reset",
  "mapa.anonPin.title",
  "mapa.anonPin.subtitle",
  "mapa.userPin.title",
  "mapa.userPin.subtitle",
] as const;

function MapTranslationsQA() {
  const rows = MAP_UI_KEYS.map((key) => {
    const values = LANGS.map(({ code }) => {
      const dict = getDict(code);
      const raw = dict[key];
      const resolved = translateStatic(code, key);
      const status: "ok" | "fallback" | "missing" =
        raw != null && raw.trim().length > 0
          ? "ok"
          : resolved !== key
            ? "fallback"
            : "missing";
      return { code, resolved, status };
    });
    return { key, values };
  });

  const missingCount = rows.reduce(
    (n, r) => n + r.values.filter((v) => v.status === "missing").length,
    0,
  );
  const fallbackCount = rows.reduce(
    (n, r) => n + r.values.filter((v) => v.status === "fallback").length,
    0,
  );

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6">
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl">QA · Překlady mapy</h1>
          <p className="text-sm text-muted-foreground">
            Náhled klíčů použitých na <code>/mapa</code> napříč všemi{" "}
            {LANGS.length} jazyky. Slouží k rychlé kontrole formulací.
          </p>
        </div>
        <div className="flex gap-2 text-xs">
          <Badge tone="ok">{rows.length * LANGS.length - missingCount - fallbackCount} OK</Badge>
          <Badge tone="warn">{fallbackCount} fallback (CS)</Badge>
          <Badge tone="err">{missingCount} chybí</Badge>
        </div>
      </header>

      <div className="overflow-auto rounded-md border">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 bg-muted/60 backdrop-blur">
            <tr>
              <th className="sticky left-0 z-10 border-b border-r bg-muted/60 px-3 py-2 text-left font-mono">
                klíč
              </th>
              {LANGS.map(({ code, label }) => (
                <th key={code} className="border-b border-r px-3 py-2 text-left font-semibold uppercase">
                  <div>{code}</div>
                  <div className="text-[10px] font-normal text-muted-foreground">{label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="odd:bg-background even:bg-muted/20">
                <td className="sticky left-0 z-10 border-b border-r bg-inherit px-3 py-2 align-top font-mono text-[11px] text-muted-foreground">
                  {row.key}
                </td>
                {row.values.map((v) => (
                  <td
                    key={v.code}
                    className={`border-b border-r px-3 py-2 align-top ${
                      v.status === "missing"
                        ? "bg-red-50 text-red-900"
                        : v.status === "fallback"
                          ? "bg-amber-50 text-amber-900"
                          : ""
                    }`}
                    title={
                      v.status === "missing"
                        ? "Chybí ve všech slovnících — zobrazí se surový klíč"
                        : v.status === "fallback"
                          ? "Chybí v této jazykové verzi — použit fallback z CS"
                          : ""
                    }
                  >
                    {v.resolved}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Legenda:{" "}
        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-900">
          fallback z CS
        </span>{" "}
        · <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-900">chybějící klíč</span>.
        Stránka je pouze pro interní kontrolu (<code>noindex</code>).
      </p>
    </div>
  );
}

function Badge({ tone, children }: { tone: "ok" | "warn" | "err"; children: React.ReactNode }) {
  const cls =
    tone === "ok"
      ? "bg-emerald-100 text-emerald-900"
      : tone === "warn"
        ? "bg-amber-100 text-amber-900"
        : "bg-red-100 text-red-900";
  return <span className={`rounded-full px-2 py-0.5 font-medium ${cls}`}>{children}</span>;
}
